import { API_URL, createHeaders } from './base'

export interface ToolCall {
  name: string
  input: Record<string, unknown>
  output: string | null
  ts: string
}

export interface WorkflowSummary {
  id: number
  title: string
  status: string
  priority: string
}

export interface AgentRun {
  id: number
  workspace_id: number
  schedule_id: number | null
  agent_name: string
  trigger: 'schedule' | 'manual'
  status: 'running' | 'completed' | 'failed'
  started_at: string
  completed_at: string | null
  prompt: string
  reasoning: string | null
  tool_calls: ToolCall[]
  created_workflow_ids: number[]
  error: string | null
  session_id: string | null
}

export interface AgentRunDetail extends AgentRun {
  workflows: WorkflowSummary[]
}

export class AgentRunConflictError extends Error {
  constructor(agentName: string) {
    super(`Agent ${agentName} is already running`)
    this.name = 'AgentRunConflictError'
  }
}

export async function listAgentRuns(params?: {
  agent_name?: string
  limit?: number
}): Promise<AgentRun[]> {
  const query = new URLSearchParams()
  if (params?.agent_name) query.set('agent_name', params.agent_name)
  if (params?.limit != null) query.set('limit', String(params.limit))
  const qs = query.toString() ? `?${query}` : ''
  const response = await fetch(`${API_URL}/agent-runs${qs}`, {
    headers: createHeaders(),
  })
  if (!response.ok) throw new Error('Failed to fetch agent runs')
  return response.json()
}

export async function getAgentRun(id: number): Promise<AgentRunDetail> {
  const response = await fetch(`${API_URL}/agent-runs/${id}`, {
    headers: createHeaders(),
  })
  if (!response.ok) throw new Error('Failed to fetch agent run detail')
  return response.json()
}

export async function triggerAgentRun(agentName: string): Promise<{ id: number }> {
  const response = await fetch(`${API_URL}/agent-runs/trigger`, {
    method: 'POST',
    headers: createHeaders({ 'Content-Type': 'application/json' }),
    body: JSON.stringify({ agent_name: agentName }),
  })
  if (response.status === 409) {
    throw new AgentRunConflictError(agentName)
  }
  if (!response.ok) {
    const text = await response.text().catch(() => '')
    throw new Error(text || 'Failed to trigger agent run')
  }
  return response.json()
}
